import React, { useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import trimCanvas from 'trim-canvas';
import styles from './signature.module.scss';

const KoSignature = ({
  clearOnResize = true,
  penColor = 'black',
  backgroundColor = 'rgba(255,255,255,0)',
  onSave = () => {},
  canvasProps = {}
}) => {
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);

  // Initialize signature pad
  useEffect(() => {
    if (!canvasRef.current) return;

    sigPadRef.current = new SignaturePad(canvasRef.current, {
      penColor,
      backgroundColor,
      onEnd: handleEnd
    });

    resizeCanvas();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      sigPadRef.current?.off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTrimmedCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const copy = document.createElement('canvas');
    copy.width = canvas.width;
    copy.height = canvas.height;
    copy.getContext('2d').drawImage(canvas, 0, 0);
    return trimCanvas(copy);
  }, []);

  const handleEnd = useCallback(() => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) return;
    const trimmed = getTrimmedCanvas();
    const dataURL = trimmed.toDataURL('image/png');
    onSave(dataURL);
  }, [getTrimmedCanvas, onSave]);

  const handleResize = useCallback(() => {
    if (!clearOnResize) return;
    resizeCanvas();
  }, [clearOnResize]);

  const resizeCanvas = useCallback(() => {
    if (!canvasRef.current) return;

    const { width, height } = canvasProps;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    // Use provided width/height or fallback to parent size
    if (width) {
      canvas.width = parseInt(width, 10) * ratio;
    } else {
      canvas.width = canvas.offsetWidth * ratio;
    }

    if (height) {
      canvas.height = parseInt(height, 10) * ratio;
    } else {
      canvas.height = canvas.offsetHeight * ratio;
    }

    canvas.getContext('2d').scale(ratio, ratio);
    sigPadRef.current?.clear();
  }, [canvasProps]);

  const clear = useCallback(() => {
    sigPadRef.current?.clear();
    onSave(null);
  }, [onSave]);

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.canvasContainer}
        style={{
          width: canvasProps.width || '500px',
          height: canvasProps.height || '200px'
        }}
      >
        <canvas
          ref={canvasRef}
          {...canvasProps}
          className={styles.canvas}
        />
      </div>
      <div className={styles.controls}>
        <button type="button" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
};

export default KoSignature;
